import React, { useEffect, useState } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, Button,
  VStack, HStack, Box, Text, Table, Thead, Tbody, Tr, Th, Td, Badge, Spinner,
  Select, useToast,
} from '@chakra-ui/react';
import deportesApi, { Convocatoria, Inscripcion } from '../../../api/deportesApi';
import { ROJO, fmtFecha, nombreCompleto } from '../_shared';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  convocatoria: Convocatoria | null;
}

const ESTATUS_COLOR: Record<string, string> = { Recibida: 'yellow', Aceptada: 'green', Rechazada: 'red' };

const ModalInscripciones: React.FC<Props> = ({ isOpen, onClose, convocatoria }) => {
  const toast = useToast();
  const [data, setData] = useState<Inscripcion[]>([]);
  const [cargando, setCargando] = useState(false);
  const [fEstatus, setFEstatus] = useState('');
  const [procesandoId, setProcesandoId] = useState<number | null>(null);

  const cargar = async () => {
    if (!convocatoria) return;
    setCargando(true);
    try {
      const res = await deportesApi.inscripcionesDeConvocatoria(convocatoria.id, fEstatus ? { estatus: fEstatus } : undefined);
      setData(res || []);
    } catch { setData([]); } finally { setCargando(false); }
  };

  useEffect(() => { if (isOpen) cargar(); /* eslint-disable-next-line */ }, [isOpen, convocatoria, fEstatus]);

  const aceptar = async (i: Inscripcion) => {
    setProcesandoId(i.id);
    try { await deportesApi.aceptarInscripcion(i.id); toast({ title: 'Inscripción aceptada', status: 'success', duration: 2000 }); cargar(); }
    catch (e: any) { toast({ title: 'Error', description: e?.response?.data?.message, status: 'error', duration: 3000 }); }
    finally { setProcesandoId(null); }
  };
  const rechazar = async (i: Inscripcion) => {
    setProcesandoId(i.id);
    try { await deportesApi.rechazarInscripcion(i.id); toast({ title: 'Inscripción rechazada', status: 'info', duration: 2000 }); cargar(); }
    catch (e: any) { toast({ title: 'Error', description: e?.response?.data?.message, status: 'error', duration: 3000 }); }
    finally { setProcesandoId(null); }
  };

  // Vincular: crea un deportista a partir de los datos de la inscripción
  const vincular = async (i: Inscripcion) => {
    setProcesandoId(i.id);
    try {
      const fd = new FormData();
      const campos: Record<string, any> = {
        nombre: i.nombre, apPaterno: i.apPaterno, apMaterno: i.apMaterno,
        fechaNacimiento: i.fechaNacimiento ? i.fechaNacimiento.split('T')[0] : '',
        sexo: i.sexo, curp: i.curp, telefono: i.telefono, email: i.email,
        localidadId: i.localidadId, localidad: i.localidad,
      };
      Object.entries(campos).forEach(([k, v]) => fd.append(k, (v ?? '') as string));
      await deportesApi.crearDeportista(fd);
      toast({ title: 'Deportista registrado desde la inscripción', status: 'success', duration: 3000 });
    } catch (e: any) {
      toast({ title: 'Error al vincular', description: e?.response?.data?.message, status: 'error', duration: 3000 });
    } finally { setProcesandoId(null); }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="5xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader borderBottom="1px solid" borderColor="gray.200" color={ROJO}>
          Inscripciones recibidas — {convocatoria?.nombre}
        </ModalHeader>
        <ModalBody py={4}>
          <HStack mb={3} justify="space-between">
            <Select size="sm" maxW="200px" placeholder="Todos los estatus" value={fEstatus} onChange={(e) => setFEstatus(e.target.value)}>
              <option value="Recibida">Recibida</option>
              <option value="Aceptada">Aceptada</option>
              <option value="Rechazada">Rechazada</option>
            </Select>
            <Text fontSize="sm" color="gray.500">{data.length} inscripción(es)</Text>
          </HStack>
          {cargando ? (
            <VStack py={8}><Spinner color={ROJO} /></VStack>
          ) : data.length === 0 ? (
            <Box py={8} textAlign="center"><Text color="gray.400">Sin inscripciones</Text></Box>
          ) : (
            <Box overflowX="auto">
              <Table size="sm" variant="simple">
                <Thead bg={ROJO}>
                  <Tr>
                    <Th color="white">Folio</Th><Th color="white">Aspirante</Th><Th color="white">CURP</Th>
                    <Th color="white">Localidad</Th><Th color="white">Fecha</Th><Th color="white">Vínculo</Th>
                    <Th color="white">Estatus</Th><Th color="white">Acciones</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {data.map((i, idx) => (
                    <Tr key={i.id} bg={idx % 2 === 0 ? 'white' : 'gray.50'}>
                      <Td fontSize="xs">{i.folio || '—'}</Td>
                      <Td><Text fontSize="sm" fontWeight="medium">{nombreCompleto(i)}</Text>{i.email && <Text fontSize="2xs" color="gray.400">{i.email}</Text>}</Td>
                      <Td fontSize="xs">{i.curp || '—'}</Td>
                      <Td fontSize="sm">{i.localidad || '—'}</Td>
                      <Td fontSize="xs">{fmtFecha(i.fecha || i.fechaNacimiento)}</Td>
                      <Td>
                        {i.deportistaId
                          ? <Badge colorScheme="green">Vinculado</Badge>
                          : <Badge colorScheme="gray">Sin vincular</Badge>}
                      </Td>
                      <Td><Badge colorScheme={ESTATUS_COLOR[i.estatus || ''] || 'gray'}>{i.estatus}</Badge></Td>
                      <Td>
                        <HStack spacing={1}>
                          {i.estatus === 'Recibida' && (
                            <>
                              <Button size="xs" colorScheme="green" isLoading={procesandoId === i.id} onClick={() => aceptar(i)}>Aceptar</Button>
                              <Button size="xs" colorScheme="red" variant="outline" isLoading={procesandoId === i.id} onClick={() => rechazar(i)}>Rechazar</Button>
                            </>
                          )}
                          {!i.deportistaId && (
                            <Button size="xs" colorScheme="teal" variant="outline" isLoading={procesandoId === i.id} onClick={() => vincular(i)}>
                              Registrar deportista
                            </Button>
                          )}
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </ModalBody>
        <ModalFooter><Button onClick={onClose}>Cerrar</Button></ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ModalInscripciones;
